﻿import { Fluent, RemoteView, htmlEncode, localText, tryGetText } from "@serenity-is/corelib";
import { Column, EventEmitter, EventSubscriber, Grid } from "@serenity-is/sleekgrid";

export enum HeaderFilterType {
    disabled = 0,
    value = 1,
    text = 2
}

declare module "@serenity-is/sleekgrid" {
    interface Column<TItem = any> {
        headerFilterType?: HeaderFilterType;
        headerFilterValues?: object[];
    }
}

const isFilteredClass = "is-filtered"

/*
    Based on SlickGrid Header Menu Plugin (https://github.com/mleibman/SlickGrid/blob/master/plugins/slick.headermenu.js)
    Based on SlickGrid Spreadsheet Plugins (https://github.com/danny-sg/slickgrid-spreadsheet-plugins/blob/master/ext.headerfilter.js)
    (Can't be used at the same time as the header menu plugin as it implements the dropdown in the same way)
*/

export interface HeaderFiltersOptions {
    buttonClass?: string;
    useColumnFormatter?: boolean;
    getFilterValues?: (column: Column, callback: (values: object[], texts?: string[]) => void) => void;
    getFilterType?: (column: Column) => HeaderFilterType;
}

export class HeaderFiltersPlugin {

    declare private grid: Grid;
    declare private menu: Fluent;
    declare private searchInput: Fluent;
    declare private handler: EventSubscriber;
    declare private options: HeaderFiltersOptions;
    private checkedValues = new Set<object>();
    declare private filterValues: object[];
    declare private filterMarkups: string[];
    public onFilterApplied = new EventEmitter();
    public onCommand = new EventEmitter();

    constructor(options: HeaderFiltersOptions) {
        this.handler = new EventSubscriber();

        var defaults: HeaderFiltersOptions = {
            buttonClass: "headerfilterbutton",
            useColumnFormatter: true,
            getFilterValues: null
        };

        this.options = Object.assign({}, defaults, options);
        this.handleMenuItemClick = this.handleMenuItemClick.bind(this);
    }

    init(g: Grid) {

        this.grid = g;
        this.handler.subscribe(g.onHeaderCellRendered, this.handleHeaderCellRendered.bind(this))
            .subscribe(g.onBeforeHeaderCellDestroy, this.handleBeforeHeaderCellDestroy.bind(this))
            .subscribe(g.onClick, this.handleBodyMouseDown.bind(this))
            .subscribe(g.onColumnsResized, this.columnsResized.bind(this));

        g.setColumns(g.getColumns());

        Fluent.on(document.body, "mousedown", this.handleBodyMouseDown = this.handleBodyMouseDown.bind(this));
    }

    destroy() {
        this.handler.unsubscribeAll();
        Fluent.off(document.body, "mousedown", this.handleBodyMouseDown);
    }

    getFilterType(column: Column) {
        if (column.headerFilterType != null)
            return column.headerFilterType;

        if (this.options.getFilterType != null) {
            column.headerFilterType = this.options.getFilterType(column);
            if (column.headerFilterType != null)
                return column.headerFilterType;
        }

        if (column.headerFilterType == null)
            column.headerFilterType = HeaderFilterType.value;

        return column.headerFilterType;
    }

    private handleBodyMouseDown(e: any) {
        if (this.menu && this.menu != e.target && !this.menu.getNode().contains(e.target)) {
            this.hideMenu();
        }
    }

    private hideMenu() {
        if (this.menu) {
            this.menu.remove();
            this.menu = null;
        }
    }

    private handleHeaderCellRendered(_: any, args: any) {
        var column = args.column as Column;

        var filterType = this.getFilterType(column);
        if (filterType === HeaderFilterType.disabled)
            return false;

        Fluent.addClass(args.node, 'has-header-menubutton');

        Fluent(<span data-columnid={column.id} class={["slick-header-menubutton s-header-filter-button",
            column.headerFilterValues != null && column.headerFilterValues.length > 0 && isFilteredClass]} onClick={e => {
                e.stopPropagation();
                this.showFilter(e);
            }} />).appendTo(args.node);
    }

    private handleBeforeHeaderCellDestroy(e: any, args: any) {
        Fluent(args.node).findFirst(".s-header-filter-button").remove();
    }

    private updateFilterItems(columnDef: Column) {
        this.checkedValues = new Set(columnDef.headerFilterValues || []);

        var filter = this.menu.findFirst('.filter');
        filter.empty();
        for (var i = -1; i < this.filterValues.length; i++) {
            const checked = i !== -1 && this.checkedValues.has(this.filterValues[i]);
            const label = <label>
                <input class={checkboxClass} type="checkbox" value={i} checked={!!checked} />
                {i === -1 && localText("Site.HeaderFiltersMixin.SelectAll", "(Select All)")}
            </label>
            if (i !== -1) {
                for (var x of cleanHTML(this.filterMarkups[i], true)) {
                    label.append(x);
                }
            }

            filter.append(label);
        }
    }

    private getFilterText(item: any, column: Column): string {
        var value = item[column.field];

        if (this.options.useColumnFormatter) {
            try {
                var format = this.grid.getFormatter(-1, column);
                if (format) {
                    var ctx = this.grid.getFormatterContext(-1, -1);
                    ctx.item = item;
                    ctx.value = value;
                    var html = format(ctx);

                    if (typeof html === "string" && html.length) {
                        html = this.grid.getOptions()?.sanitizer?.(html);
                    }

                    if (html instanceof Node) {
                        html = html instanceof Element ?
                            html.outerHTML :
                            html instanceof DocumentFragment ?
                                Array.from(html.children).map(x => x.outerHTML).join("") : '';
                    }

                    if (value == null && html === '') {
                        return (htmlEncode(tryGetText("Site.HeaderFiltersMixin.Null") || "(null)"));
                    }

                    return html;
                }
            }
            catch (ex) {
            }
        }

        if (value == null)
            return htmlEncode(localText("Site.HeaderFiltersMixin.Null", "(null)"));

        return htmlEncode(value);
    }

    private showFilter(e: Event) {
        var menuButton = Fluent(e.target);
        var columnDef = this.grid.getColumnById(menuButton.data("columnid"));
        if (!columnDef)
            return;

        this.checkedValues = new Set(columnDef.headerFilterValues || []);

        this.menu ??= Fluent(<div class="slick-header-menu s-header-filter-menu cke_dialog"></div>)
            .appendTo(document.body).hide();
        this.menu.empty();

        this.searchInput = Fluent(
            <input data-columnid={columnDef.id}
                placeholder={localText("Site.HeaderFiltersMixin.Search", "Search")}
                onKeyUp={() => this.updateFilterValues(columnDef.id)} />)
            .appendTo(this.menu);

        const filterDiv = Fluent(<div class="filter"></div>)
            .on("click", checkboxSelector, this.onCheckboxClick.bind(this))
            .appendTo(this.menu);

        this.menu.append(
            <div className="buttons">
                <button className="btn btn-success" onClick={(ev) => {
                    columnDef.headerFilterValues = Array.from(this.checkedValues);
                    menuButton.toggleClass(isFilteredClass, columnDef.headerFilterValues.length > 0);
                    this.handleApply(ev, columnDef);
                }}>
                    {localText("Site.HeaderFiltersMixin.OkButton", "OK")}
                </button>
                <button className="btn btn-danger" onClick={(ev) => {
                    if (columnDef.headerFilterValues)
                        columnDef.headerFilterValues.length = 0;
                    menuButton.removeClass(isFilteredClass);
                    this.handleApply(ev, columnDef);
                }}>
                    {localText("Site.HeaderFiltersMixin.ClearButton", "Clear")}
                </button>
                <button className="btn btn-secondary" onClick={this.hideMenu.bind(this)}>
                    {localText("Site.HeaderFiltersMixin.CancelButton", "Cancel")}
                </button>
            </div>
        );

        this.updateFilterValues(columnDef.id, () => {
            this.menu.show();
            filterDiv.style(s => delete s.height);
            var menuBounds = this.menu.getNode().getBoundingClientRect();
            var buttonBounds = (e.target as HTMLElement).getBoundingClientRect();
            var offset = getOffset(e.target as HTMLElement);
            var left = offset.left - menuBounds.width + buttonBounds.width - 8;
            var menuTop = offset.top + buttonBounds.height;

            var windowScrollTop = window.scrollY;
            var bottomBoundary = window.innerHeight + windowScrollTop;
            var contentWrapperTop = parseInt(document.querySelector<HTMLElement>('.content-wrapper')?.style?.paddingTop ?? "0", 10) || 0;

            if (menuTop + menuBounds.height > bottomBoundary &&
                bottomBoundary - menuTop + contentWrapperTop < menuTop - windowScrollTop) {
                menuTop -= (menuBounds.height + buttonBounds.height + 8);

                if (menuTop < windowScrollTop)
                    menuTop = windowScrollTop;
            }

            var extraHeight = menuTop + menuBounds.height - bottomBoundary;

            if (extraHeight > 0) {
                const filterHeight = filterDiv.getNode().getBoundingClientRect().height - extraHeight + "px";
                filterDiv.style(s => s.height = filterHeight + "px");
            }

            this.menu.style(s => {
                s.top = menuTop + "px";
                s.left = (left > 0 ? left : 0) + "px"
            });
        });
    }

    private columnsResized() {
        this.hideMenu();
    }

    private onCheckboxClick(e: Event) {
        var checkbox = e.target as HTMLInputElement;
        var index = parseInt(checkbox.value, 10);
        var filterDiv = checkbox.closest(".filter");

        if (index === -1) {
            Fluent(filterDiv).findAll<HTMLInputElement>(checkboxSelector).forEach(x => x !== checkbox && (x.checked = checkbox.checked));
            if (checkbox.checked)
                this.checkedValues = new Set(this.filterValues || []);
            else
                this.checkedValues.clear();
        } else {
            var value = this.filterValues[index];
            var has = this.checkedValues.has(value);

            if (checkbox.checked && !has) {
                this.checkedValues.add(value);
            }
            else if (has) {
                this.checkedValues.delete(value);
            }
        }
    }

    private handleApply(e: any, columnDef: Column) {
        this.hideMenu();

        this.onFilterApplied.notify({
            "grid": this.grid,
            "column": columnDef
        }, e, this);

        e.preventDefault();
        e.stopPropagation();
    }

    private containsFilter(filter: string): ((text: string) => boolean) {
        if (filter == null && filter.length == 0)
            return v => true;

        var lowercaseFilter = htmlEncode(filter).toLowerCase();

        return (text: string) => {
            text = (text ?? '').toString().replace(/<[^>]+>/g, '').toLowerCase();
            return text.indexOf(lowercaseFilter) >= 0;
        };
    }

    public getFilterValue(item: any, column: Column) {
        var value = item[column.field];
        if (this.options.useColumnFormatter &&
            column.formatter != null &&
            column.headerFilterType == HeaderFilterType.text) {
            return (this.getFilterText(item, column)
                .replace(/<[^>]+>/g, ''));
        }

        return value;
    }

    private sortFilterValues() {
        var idx: number[] = [];
        for (var i = 0; i < this.filterValues.length; i++) {
            idx[i] = i;
        }
        idx.sort((x, y) => {
            var A = this.filterValues[x];
            var B = this.filterValues[y];
            return (A < B ? -1 : (A > B ? 1 : 0));
        });
        var newFilterValues = [];
        var newFilterTexts = [];
        for (var i = 0; i < idx.length; i++) {
            newFilterValues.push(this.filterValues[i]);
            newFilterTexts.push(this.filterMarkups[i]);
        }
        this.filterValues = newFilterValues;
        this.filterMarkups = newFilterTexts;
    }

    private updateFilterValues(columnId: string, done?: () => void) {
        let column = this.grid.getColumnById(columnId);
        if (!column)
            return;
        if (this.options.getFilterValues != null) {
            this.options.getFilterValues(column, (values: object[], texts: string[]) => {
                if (values == null) {
                    this.updateFilterValuesFromData(column);
                    this.updateFilterItems(column);
                    done?.();
                }
                else {
                    this.filterValues = [];
                    this.filterMarkups = [];

                    texts = texts || values.map(x => {
                        var item: any = {};
                        item[column.field] = x;
                        return this.getFilterText(item, column);
                    });

                    var filter = this.containsFilter(this.searchInput.val() as string);
                    this.filterValues = values.filter((x, i) => {
                        if (filter(texts[i])) {
                            this.filterMarkups.push(texts[i]);
                            return true;
                        }

                        return false;
                    });

                    this.sortFilterValues();
                    this.updateFilterItems(column);
                    done?.();
                }
            });
        }
        else {
            this.updateFilterValuesFromData(column);
            this.updateFilterItems(column);
            done?.();
        }
    }

    private updateFilterValuesFromData(column: Column) {

        this.filterValues = [];
        this.filterMarkups = [];
        var dataView = ((this.grid.getData() as any) as RemoteView<any>);

        var items: any[];
        if (this.checkedValues && this.checkedValues.size) {
            items = dataView.getItems();
        }
        else {
            items = [];
            for (var i = 0; i < dataView.getLength(); i++) {
                items.push(dataView.getItem(i));
            }
        }

        var seen = new Set();
        var searchText = this.searchInput.val() as string;
        var searchFilter = this.containsFilter(searchText);
        for (var item of items) {
            var value = this.getFilterValue(item, column);
            if (!seen.has(value)) {
                var text = this.getFilterText(item, column);
                if (searchFilter(text)) {
                    seen.add(value);
                    this.filterValues.push(value);
                    this.filterMarkups.push(text);
                }
            }
        }

        this.sortFilterValues();
    }

    private handleMenuItemClick(e: any) {
        var command = Fluent(e.target).data("command");
        var column = this.grid.getColumnById(Fluent(e.target).data("columnid"));
        if (!column)
            return;

        this.hideMenu();

        this.onCommand.notify({
            grid: this,
            column: column,
            command: command
        }, e, this);

        e.preventDefault();
        e.stopPropagation();
    }
}

const checkboxClass = "hfp-checkbox";
const checkboxSelector = "input[type=checkbox]." + checkboxClass;

typeof globalThis != "undefined" && globalThis["Slick"] && globalThis["Slick"]["Plugins"] && (globalThis.Slick["Plugins"]["HeaderFilters"] = HeaderFiltersPlugin);

function getOffset(el: Element) {
    var box = el.getBoundingClientRect();
    var docElem = document.documentElement;
    return {
        top: box.top + window.scrollY - docElem.clientTop,
        left: box.left + window.scrollX - docElem.clientLeft
    };
}

/**
 * Sanitize an HTML string
 * (c) Chris Ferdinandi, MIT License, https://gomakethings.com
 */
function cleanHTML(str: string, nodes: boolean): string | Array<Node> {

    function stringToHTML(): Element {
        let parser = new DOMParser();
        let doc = parser.parseFromString(str, 'text/html');
        return doc.body || document.createElement('body');
    }

    function removeScripts(html: Element) {
        let scripts = html.querySelectorAll<HTMLScriptElement>('script');
        for (let script of Array.from(scripts)) {
            script.remove();
        }
    }

    function isPossiblyDangerous(name: string, value: string): boolean {
        let val = value.replace(/\s+/g, '').toLowerCase();
        if (['src', 'href', 'xlink:href'].includes(name)) {
            if (val.includes('javascript:') || val.includes('data:')) return true;
        }
        if (name.startsWith('on')) return true;
    }

    function removeAttributes(elem: Node) {
        // Loop through each attribute
        // If it's dangerous, remove it
        let attrs = (elem as Element).attributes;
        for (var i = 0; i < attrs.length; i++) {
            var attr = attrs[i];
            // remove edit links and others
            if (attr.name?.toLowerCase() === 'href') {
                (elem as Element).removeAttribute(attr.name);
                continue;
            }
            if (!isPossiblyDangerous(attr.name, attr.value)) continue;
            (elem as Element).removeAttribute(attr.name);
        }
    }

    /**
     * Remove dangerous stuff from the HTML document's nodes
     * @param  {Node} html The HTML document
     */
    function clean(html: Element) {
        let nodes = html.children;
        if (nodes)
            for (var node of Array.from(nodes)) {
                removeAttributes(node);
                clean(node);
            }
    }

    // Convert the string to HTML
    let html = stringToHTML();

    // Sanitize it
    removeScripts(html);
    clean(html);

    // If the user wants HTML nodes back, return them
    // Otherwise, pass a sanitized string back
    return nodes ? Array.from(html.childNodes) : html.innerHTML;
}